// src/components/admin/DoctorVerificationDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';
import DocumentViewerModal from './DocumentViewerModal';
import '../../styles/components/admin/DoctorVerificationDetails.css';

import personalIcon from '../../assets/icons/indicators/PersonalInfo_icon.png';
import professionalIcon from '../../assets/icons/indicators/professionals_indicator.png';
import availabilityIcon from '../../assets/icons/indicators/availability_indicator.png';
import credentialsIcon from '../../assets/icons/indicators/credentials_indicator.png';

const DoctorVerificationDetails = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('mode') === 'edit';
  
  // 🆕 ADD THIS: State for edit mode
  const [editedDoctor, setEditedDoctor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationAction, setVerificationAction] = useState('');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  
  // Document viewer modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  useEffect(() => {
    console.log('🟢 DoctorVerificationDetails - Loading doctor:', doctorId);
    fetchDoctorDetails();
  }, [doctorId]);

  useEffect(() => {
  console.log('🔍 DEBUG: useEffect triggered');
  console.log('🔍 DEBUG: doctor state:', doctor);
  console.log('🔍 DEBUG: doctor is null?', doctor === null);
  console.log('🔍 DEBUG: doctor is undefined?', doctor === undefined);
  
  if (doctor) {
    console.log('✅ DOCTOR EXISTS - Full object:', JSON.stringify(doctor, null, 2));
    console.log('🔍 Document fields:', {
      licenseDocumentUrl: doctor.licenseDocumentUrl,
      educationCertificateUrl: doctor.educationCertificateUrl,
      diplomaUrl: doctor.diplomaUrl,
      resumeUrl: doctor.resumeUrl,
      additionalDocumentUrls: doctor.additionalDocumentUrls,
      verificationDocuments: doctor.verificationDocuments
    });
  } else {
    console.log('❌ NO DOCTOR DATA YET');
  }
}, [doctor]);

      const fetchDoctorDetails = async () => {
  try {
    setLoading(true);
    setError(null);
    
    console.log('🔵 FETCHING doctor details for ID:', doctorId);
    const response = await adminService.getDoctorDetails(doctorId);
    console.log('✅ RAW API RESPONSE:', response);
    console.log('✅ RESPONSE DATA:', response.data);
    
    console.log('🔍 FRONTEND - Document URLs received:', {
      licenseDocumentUrl: response.data?.licenseDocumentUrl,
      educationCertificateUrl: response.data?.educationCertificateUrl,
      additionalDocumentUrls: response.data?.additionalDocumentUrls,
      profilePicture: response.data?.profilePicture
    });
    
    setDoctor(response.data);
    // 🆕 ADD THIS: Initialize editedDoctor when in edit mode
    if (isEditMode) {
      setEditedDoctor({ ...response.data });
    }
  } catch (err) {
    console.error('❌ Error fetching doctor details:', err);
    setError('Failed to load doctor details. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const handleVerification = async (status, rejectionReason = '') => {
  try {
    setSubmitting(true);
    setError(null);
    
    // Prepare verification data
    const verificationData = {
      verificationStatus: status,
      verificationNotes: '',  // We're not using notes anymore
      rejectionReason: status === 'rejected' ? rejectionReason : ''
    };
    
    await adminService.verifyDoctor(doctorId, verificationData);
    
    const successMessage = `Doctor application has been ${status === 'approved' ? 'approved' : 'rejected'} successfully!`;
    toast.success(successMessage);
    
    // Navigate back to pending doctors list
    navigate('/admin/professionals');
    
  } catch (err) {
    console.error('❌ Verification error:', err);
    const errorMessage = err.response?.data?.message || 'Verification process failed. Please try again.';
    setError(errorMessage);
  } finally {
    setSubmitting(false);
  }
};


const handleRejection = () => {
  setIsRejectionModalOpen(true);
};

const handleRejectionSubmit = (reason) => {
  if (!reason || reason.trim() === '') {
    setError('A reason is required when rejecting an application.');
    return;
  }
  
  // Call verification with rejected status and the provided reason
  handleVerification('rejected', reason);
  setIsRejectionModalOpen(false);
};

const handleCancelEdit = () => {
  navigate(`/admin/professionals/${doctorId}`);
};

const handleSaveEdit = async () => {
  try {
    setIsSaving(true);
    setError(null);
    
    console.log('💾 Saving doctor edits:', editedDoctor);
    
    await adminService.updateDoctor(doctorId, editedDoctor, doctor.tenantId);
    
    toast.success('Doctor profile updated successfully!');
    
    await fetchDoctorDetails();
    
    navigate(`/admin/professionals/${doctorId}`);
    
  } catch (err) {
    console.error('❌ Error saving doctor:', err);
    const errorMessage = err.response?.data?.message || 'Failed to save changes. Please try again.';
    setError(errorMessage);
    toast.error(errorMessage);
  } finally {
    setIsSaving(false);
  }
};


const handleFieldChange = (field, value) => {
  setEditedDoctor(prev => ({
    ...prev,
    [field]: value
  }));
};

// 🆕 ADD ALL THESE FUNCTIONS RIGHT HERE - AFTER handleFieldChange
const handleEducationChange = (index, field, value) => {
  setEditedDoctor(prev => {
    const newEducation = [...(prev.education || [])];
    newEducation[index] = {
      ...newEducation[index],
      [field]: value
    };
    return {
      ...prev,
      education: newEducation
    };
  });
};

const handleCertificationChange = (index, field, value) => {
  setEditedDoctor(prev => {
    const newCertifications = [...(prev.certifications || [])];
    newCertifications[index] = {
      ...newCertifications[index],
      [field]: value
    };
    return {
      ...prev,
      certifications: newCertifications
    };
  });
};

const addEducation = () => {
  setEditedDoctor(prev => ({
    ...prev,
    education: [...(prev.education || []), { degree: '', institution: '', year: '' }]
  }));
};

const removeEducation = (index) => {
  setEditedDoctor(prev => ({
    ...prev,
    education: prev.education.filter((_, i) => i !== index)
  }));
};

const addCertification = () => {
  setEditedDoctor(prev => ({
    ...prev,
    certifications: [...(prev.certifications || []), { name: '', issuingAuthority: '', year: '' }]
  }));
};

const removeCertification = (index) => {
  setEditedDoctor(prev => ({
    ...prev,
    certifications: prev.certifications.filter((_, i) => i !== index)
  }));
};

  // Document viewer functions
  const openDocumentModal = (url, title) => {
    if (!url) {
      toast.error('Document not available');
      return;
    }
    
    setCurrentDocument({
      url: url,
      title: title,
      type: getDocumentType(url)
    });
    setIsModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsModalOpen(false);
    setCurrentDocument(null);
  };

  const getDocumentType = (url) => {
    if (!url) return 'unknown';
    
    const extension = url.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) return 'image';
    if (['doc', 'docx'].includes(extension)) return 'document';
    return 'unknown';
  };

  const extractFilenameFromUrl = (url) => {
  if (!url) return 'Document';
  
  // Extract filename from Cloudinary URL
  try {
    // Get the last part of the URL (after the last slash)
    const fullFilename = url.split('/').pop();
    const parts = fullFilename.split('_');
    if (parts.length > 1) {
      return parts.slice(parts.length - 1).join('_');
    }
    
    // Fallback to the full filename
    return fullFilename;
  } catch (err) {
    console.error('Error extracting filename:', err);
    return 'Document';
  }
};

  // 🔧 SAFE: Helper function to safely render any value
  const safeRender = (value, fallback = 'Not provided') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : fallback;
      }
      return JSON.stringify(value);
    }
    
    return String(value);
  };

  // 🔧 SAFE: Helper function to safely render array data
  const safeRenderArray = (array, fallback = 'Not specified') => {
    if (!Array.isArray(array) || array.length === 0) {
      return fallback;
    }
    
    return array.map((item, index) => {
      if (typeof item === 'string') {
        return item;
      }
      if (typeof item === 'object' && item.name) {
        return item.name;
      }
      if (typeof item === 'object' && item.content) {
        return item.content;
      }
      return String(item);
    }).join(', ');
  };

  // 🔧 SAFE: Helper function to safely render verification notes
  const safeRenderVerificationNotes = (notes) => {
    if (!notes) return null;
    
    if (Array.isArray(notes)) {
      return notes.map((note, index) => (
        <p key={index} className="dvd-verification-note">
          {typeof note === 'object' ? (note.note || note.content || JSON.stringify(note)) : String(note)}
        </p>
      ));
    }
    
    if (typeof notes === 'object') {
      return <p className="dvd-verification-note">{notes.note || notes.content || JSON.stringify(notes)}</p>;
    }
    
    return <p className="dvd-verification-note">{String(notes)}</p>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatAvailability = (availability) => {
    if (!availability) return 'Not specified';
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const availableDays = days.filter(day => availability[day]?.available);
    
    if (availableDays.length === 0) return 'No availability set';
    
    return availableDays.map(day => 
      day.charAt(0).toUpperCase() + day.slice(1)
    ).join(', ');
  };

  const renderAvailabilitySchedule = (availability) => {
    if (!availability) return <p className="dvd-no-data">No availability information provided</p>;
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    const availableDays = days.filter(day => availability[day]?.available);
    
    if (availableDays.length === 0) {
      return <p className="dvd-no-data">No available days set</p>;
    }
    
    return (
      <div className="dvd-availability-schedule">
        {days.map((day, index) => {
          const dayData = availability[day];
          if (!dayData?.available) return null;
          
          return (
            <div key={day} className="dvd-availability-day">
              <div className="dvd-day-name">{dayLabels[index]}</div>
              <div className="dvd-time-slots">
                {dayData.slots && dayData.slots.length > 0 ? (
                  dayData.slots.map((slot, slotIndex) => (
                    <div key={slotIndex} className="dvd-time-slot">
                      <span className="dvd-start-time">{safeRender(slot.startTime, 'Not set')}</span>
                      <span className="dvd-time-separator"> - </span>
                      <span className="dvd-end-time">{safeRender(slot.endTime, 'Not set')}</span>
                    </div>
                  ))
                ) : (
                  <div className="dvd-time-slot">
                    <span className="dvd-no-times">No specific times set</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDocumentLink = (url, label) => {
    if (!url) return <span className="dvd-no-document">Not provided</span>;
    
    return (
      <div className="dvd-document-container">
        <div className="dvd-document-info">
          <span className="dvd-document-name">{label}</span>
          <span className="dvd-document-type">{getDocumentType(url).toUpperCase()}</span>
        </div>
        <div className="dvd-document-actions">
          <button 
            className="dvd-view-button"
            onClick={() => openDocumentModal(url, label)}
            title="View Document in Modal"
          >
            <svg className="dvd-view-icon" viewBox="0 0 24 24" fill="none">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
            </svg>
            View
          </button>
        </div>
      </div>
    );
  };

  // Helper function to determine if doctor needs verification
  const isPendingVerification = () => {
  if (!doctor) return false;
  
  return !doctor.verificationStatus || 
         doctor.verificationStatus === 'pending' ||
         (doctor.verificationStatus !== 'approved' && doctor.verificationStatus !== 'rejected');
};

  // Helper function to get status badge style
  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'approved':
        return 'dvd-status-verified-approved';
      case 'rejected':
        return 'dvd-status-verified-rejected';
      default:
        return 'dvd-status-pending';
    }
  };

  // Update header text based on doctor status
  const getHeaderText = () => {
  if (!doctor) return { title: 'Doctor Details', subtitle: 'Loading...' };
  
  // 🆕 ADD THIS: Show edit mode in header
  if (isEditMode) {
    return {
      subtitle: `Editing profile for ${safeRender(doctor.firstName)} ${safeRender(doctor.lastName)}`
    };
  }
  
  if (isPendingVerification()) {
    return {
      subtitle: `Registration Date: ${formatDate(doctor.createdAt || doctor.registrationDate)}`
    };
  } else {
    return {
      subtitle: `Viewing profile for ${safeRender(doctor.firstName)} ${safeRender(doctor.lastName)}`
    };
  }
};

  if (loading) {
    return (
      <div className="dvd-container">
        <div className="dvd-loading-container">
          <div className="dvd-loading-spinner"></div>
          <p className="dvd-loading-text">Loading doctor details...</p>
        </div>
      </div>
    );
  }

  if (error && !doctor) {
    return (
      <div className="dvd-container">
        <div className="dvd-error-container">
          <div className="dvd-error-header">
            <button 
              className="dvd-back-button"
              onClick={() => navigate('/admin/professionals')}
            >
              ← Back to Professionals
            </button>
          </div>
          <div className="dvd-error-message">
            <h2 className="dvd-error-title">Error Loading Doctor Details</h2>
            <p className="dvd-error-text">{error}</p>
            <button className="dvd-retry-button" onClick={fetchDoctorDetails}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const headerText = getHeaderText();

  return (
    <div className="dvd-container">
      {/* Header with back button */}
      <div className="dvd-header">
        <button 
          className="dvd-back-button"
          onClick={() => navigate('/admin/professionals')}
        >
          ←
        </button>
        <div className="dvd-header-info">
          <h1 className="dvd-title">{headerText.title}</h1>
          <p className="dvd-subtitle">
          <strong>Registration Date:</strong> {formatDate(doctor.createdAt || doctor.registrationDate)}
        </p>
        </div>
      </div>

      {error && (
        <div className="dvd-error-alert">
          <span className="dvd-error-icon">⚠️</span>
          <span className="dvd-error-alert-text">{error}</span>
        </div>
      )}

      {doctor && (
        <div className="dvd-content">
          {/* Show verification status for already-verified doctors */}
          <div className="dvd-doctor-info-section">
            {/* Personal Information */}
            <div className="dvd-info-card dvd-personal-info-section">
              <h3 className="dvd-card-title">
                <img src={personalIcon} alt="" className="dvd-section-icon" />
                Personal Information
              </h3>
              <div className="dvd-info-grid">
                <div className="dvd-info-item">
                  <label className="dvd-info-label">First Name</label>
                  {isEditMode ? (
                    <input 
                      type="text"
                      className="dvd-info-input"
                      value={editedDoctor?.firstName || ''}
                      onChange={(e) => handleFieldChange('firstName', e.target.value)}
                      placeholder="Enter first name"
                    />
                  ) : (
                    <span className="dvd-info-value">{safeRender(doctor.firstName)}</span>
                  )}
                </div>
                
                <div className="dvd-info-item">
                  <label className="dvd-info-label">Last Name</label>
                  {isEditMode ? (
                    <input 
                      type="text"
                      className="dvd-info-input"
                      value={editedDoctor?.lastName || ''}
                      onChange={(e) => handleFieldChange('lastName', e.target.value)}
                      placeholder="Enter last name"
                    />
                  ) : (
                    <span className="dvd-info-value">{safeRender(doctor.lastName)}</span>
                  )}
                </div>
                
                <div className="dvd-info-item">
                  <label className="dvd-info-label">Email Address</label>
                  {isEditMode ? (
                    <input 
                      type="email"
                      className="dvd-info-input"
                      value={editedDoctor?.email || ''}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      placeholder="Enter email address"
                    />
                  ) : (
                    <span className="dvd-info-value">{safeRender(doctor.email)}</span>
                  )}
                </div>
                
                <div className="dvd-info-item">
                  <label className="dvd-info-label">Personal Contact Number</label>
                  {isEditMode ? (
                    <input 
                      type="tel"
                      className="dvd-info-input"
                      value={editedDoctor?.personalContactNumber || ''}
                      onChange={(e) => handleFieldChange('personalContactNumber', e.target.value)}
                      placeholder="Enter contact number"
                    />
                  ) : (
                    <span className="dvd-info-value">{safeRender(doctor.personalContactNumber)}</span>
                  )}
                </div>
                
                <div className="dvd-info-item">
                  <label className="dvd-info-label">Clinic Location</label>
                  {isEditMode ? (
                    <input 
                      type="text"
                      className="dvd-info-input"
                      value={editedDoctor?.clinicLocation || editedDoctor?.clinicAddress || ''}
                      onChange={(e) => handleFieldChange('clinicLocation', e.target.value)}
                      placeholder="Enter clinic location"
                    />
                  ) : (
                    <span className="dvd-info-value">{safeRender(doctor.clinicLocation || doctor.clinicAddress)}</span>
                  )}
                </div>
                
                <div className="dvd-info-item">
                  <label className="dvd-info-label">Clinic Contact Number</label>
                  {isEditMode ? (
                    <input 
                      type="tel"
                      className="dvd-info-input"
                      value={editedDoctor?.clinicContactNumber || ''}
                      onChange={(e) => handleFieldChange('clinicContactNumber', e.target.value)}
                      placeholder="Enter clinic contact number"
                    />
                  ) : (
                    <span className="dvd-info-value">{safeRender(doctor.clinicContactNumber)}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Information */}
<div className="dvd-info-card dvd-professional-info-section">
  <h3 className="dvd-card-title">
    <img src={professionalIcon} alt="" className="dvd-section-icon" />
    Professional Information
  </h3>
  <div className="dvd-info-grid">
    <div className="dvd-info-item">
      <label className="dvd-info-label">Specialty</label>
      {isEditMode ? (
        <input 
          type="text"
          className="dvd-info-input"
          value={editedDoctor?.specialty || editedDoctor?.specialization || ''}
          onChange={(e) => handleFieldChange('specialty', e.target.value)}
          placeholder="Enter specialty"
        />
      ) : (
        <span className="dvd-info-value">{safeRender(doctor.specialty || doctor.specialization)}</span>
      )}
    </div>
    
    <div className="dvd-info-item">
      <label className="dvd-info-label">Professional Title</label>
      {isEditMode ? (
        <input 
          type="text"
          className="dvd-info-input"
          value={editedDoctor?.title || ''}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          placeholder="Enter professional title"
        />
      ) : (
        <span className="dvd-info-value">{safeRender(doctor.title)}</span>
      )}
    </div>
    
    <div className="dvd-info-item">
      <label className="dvd-info-label">Areas of Expertise</label>
      {isEditMode ? (
        <input 
          type="text"
          className="dvd-info-input"
          value={editedDoctor?.areasOfExpertise || ''}
          onChange={(e) => handleFieldChange('areasOfExpertise', e.target.value)}
          placeholder="Enter areas of expertise"
        />
      ) : (
        <span className="dvd-info-value">{safeRender(doctor.areasOfExpertise)}</span>
      )}
    </div>
    
    <div className="dvd-info-item">
      <label className="dvd-info-label">Experience</label>
      {isEditMode ? (
        <input 
          type="text"
          className="dvd-info-input"
          value={editedDoctor?.experience || editedDoctor?.yearsOfPractice || ''}
          onChange={(e) => handleFieldChange('experience', e.target.value)}
          placeholder="Enter years of experience"
        />
      ) : (
        <span className="dvd-info-value">{safeRender(doctor.experience || doctor.yearsOfPractice)}</span>
      )}
    </div>
  </div>
  
  {doctor.bio && (
    <div className="dvd-bio-section">
      <label className="dvd-bio-label">Professional Bio:</label>
      {isEditMode ? (
        <textarea 
          className="dvd-info-textarea"
          value={editedDoctor?.bio || ''}
          onChange={(e) => handleFieldChange('bio', e.target.value)}
          placeholder="Enter professional bio"
          rows={4}
        />
      ) : (
        <p className="dvd-bio-text">{safeRender(doctor.bio)}</p>
      )}
    </div>
  )}
</div>

            {/* Service Availability */}
            <div className="dvd-info-card dvd-availability-section">
              <h3 className="dvd-card-title">
                <img src={availabilityIcon} alt="" className="dvd-section-icon" />
                Availability
              </h3>
              <div className="dvd-availability-info">
                <div className="dvd-schedule-section">
                  {renderAvailabilitySchedule(doctor.availability)}
                </div>
                
                <div className="dvd-schedule-section">
                  <label className="dvd-schedule-label">Appointment Types</label>
                  <div className="dvd-service-types">
                    <div className="dvd-service-type">
                      <span className="dvd-service-badge">
                        {doctor.inPerson || doctor.appointmentTypes?.inPerson ? 'In-person, Telehealth' : 'Telehealth only'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Credentials */}
<div className="dvd-info-card dvd-credentials-section">
  <h3 className="dvd-card-title">
    <img src={credentialsIcon} alt="" className="dvd-section-icon" />
    Credentials
  </h3>
  
  {/* Education */}
  <div className="dvd-credentials-subsection">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h4 className="dvd-credentials-subtitle">Education</h4>
      {isEditMode && (
        <button 
          className="dvd-add-item-btn"
          onClick={addEducation}
          type="button"
        >
          + Add Education
        </button>
      )}
    </div>
    
    {isEditMode ? (
      <div className="dvd-credentials-list">
        {(editedDoctor?.education || []).map((edu, index) => (
          <div key={index} className="dvd-credential-item-edit">
            <div className="dvd-credential-edit-grid">
              <div className="dvd-credential-field">
                <label>Degree</label>
                <input
                  type="text"
                  className="dvd-info-input"
                  value={edu.degree || ''}
                  onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                  placeholder="e.g., BA Psychology"
                />
              </div>
              <div className="dvd-credential-field">
                <label>Institution</label>
                <input
                  type="text"
                  className="dvd-info-input"
                  value={edu.institution || ''}
                  onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                  placeholder="e.g., University of Manila"
                />
              </div>
              <div className="dvd-credential-field">
                <label>Year</label>
                <input
                  type="text"
                  className="dvd-info-input"
                  value={edu.year || ''}
                  onChange={(e) => handleEducationChange(index, 'year', e.target.value)}
                  placeholder="e.g., 2020"
                />
              </div>
            </div>
            <button 
              className="dvd-remove-item-btn"
              onClick={() => removeEducation(index)}
              type="button"
            >
              Remove
            </button>
          </div>
        ))}
        {(!editedDoctor?.education || editedDoctor.education.length === 0) && (
          <p className="dvd-no-data">No education entries. Click "Add Education" to add one.</p>
        )}
      </div>
    ) : (
      doctor.education && doctor.education.length > 0 ? (
        <div className="dvd-credentials-list">
          {doctor.education.map((edu, index) => (
            <div key={index} className="dvd-credential-item">
              <span className="dvd-credential-degree">{safeRender(edu.degree, 'Degree not specified')}</span>
              <div className="dvd-credential-institution-year">
                <span className="dvd-credential-institution">{safeRender(edu.institution, 'Institution not specified')}</span>
                <span className="dvd-credential-year">{safeRender(edu.year, 'Year not specified')}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="dvd-no-data">No education information provided</p>
      )
    )}
  </div>

  {/* Licenses */}
  <div className="dvd-credentials-subsection">
    <h4 className="dvd-credentials-subtitle">Licenses</h4>
    <div className="dvd-credential-item">
      {isEditMode ? (
        <div className="dvd-license-edit-container">
          <div className="dvd-credential-field">
            <label>License Number</label>
            <input
              type="text"
              className="dvd-info-input"
              value={editedDoctor?.licenseNumber || ''}
              onChange={(e) => handleFieldChange('licenseNumber', e.target.value)}
              placeholder="Enter license number"
            />
          </div>
          <div className="dvd-credential-field">
            <label>License Expiry Date</label>
            <input
              type="date"
              className="dvd-info-input"
              value={editedDoctor?.licenseExpiryDate ? new Date(editedDoctor.licenseExpiryDate).toISOString().split('T')[0] : ''}
              onChange={(e) => handleFieldChange('licenseExpiryDate', e.target.value)}
            />
          </div>
        </div>
      ) : (
        <>
          <span className="dvd-credential-degree">
            State Psychology License License #: {safeRender(doctor.licenseNumber)}
          </span>
          <span className="dvd-credential-year">
            Expires: {formatDate(doctor.licenseExpiryDate)}
          </span>
        </>
      )}
    </div>
  </div>

  {/* Certifications */}
  <div className="dvd-credentials-subsection">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h4 className="dvd-credentials-subtitle">Certifications</h4>
      {isEditMode && (
        <button 
          className="dvd-add-item-btn"
          onClick={addCertification}
          type="button"
        >
          + Add Certification
        </button>
      )}
    </div>
    
    {isEditMode ? (
      <div className="dvd-credentials-list">
        {(editedDoctor?.certifications || []).map((cert, index) => (
          <div key={index} className="dvd-credential-item-edit">
            <div className="dvd-credential-edit-grid">
              <div className="dvd-credential-field">
                <label>Certification Name</label>
                <input
                  type="text"
                  className="dvd-info-input"
                  value={cert.name || cert.degree || ''}
                  onChange={(e) => handleCertificationChange(index, 'name', e.target.value)}
                  placeholder="e.g., PAP Certified Clinical Psychologist"
                />
              </div>
              <div className="dvd-credential-field">
                <label>Issuing Authority</label>
                <input
                  type="text"
                  className="dvd-info-input"
                  value={cert.issuingAuthority || cert.authority || ''}
                  onChange={(e) => handleCertificationChange(index, 'issuingAuthority', e.target.value)}
                  placeholder="e.g., Psychological Association"
                />
              </div>
              <div className="dvd-credential-field">
                <label>Year</label>
                <input
                  type="text"
                  className="dvd-info-input"
                  value={cert.year || ''}
                  onChange={(e) => handleCertificationChange(index, 'year', e.target.value)}
                  placeholder="e.g., 2021"
                />
              </div>
            </div>
            <button 
              className="dvd-remove-item-btn"
              onClick={() => removeCertification(index)}
              type="button"
            >
              Remove
            </button>
          </div>
        ))}
        {(!editedDoctor?.certifications || editedDoctor.certifications.length === 0) && (
          <p className="dvd-no-data">No certifications. Click "Add Certification" to add one.</p>
        )}
      </div>
    ) : (
      doctor.certifications && doctor.certifications.length > 0 ? (
        <div className="dvd-credentials-list">
          {doctor.certifications.map((cert, index) => (
            <div key={index} className="dvd-credential-item">
              <span className="dvd-credential-degree">{safeRender(cert.degree || cert.name, 'Certification not specified')}</span>
              <div className="dvd-credential-institution-year">
                <span className="dvd-credential-institution">{safeRender(cert.issuingAuthority || cert.authority, 'Authority not specified')}</span>
                <span className="dvd-credential-year">{safeRender(cert.year, 'Year not specified')}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="dvd-no-data">No certifications provided</p>
      )
    )}
  </div>

  {/* Document Verification Section - Keep as view only */}
  <div className="dvd-document-divider"></div>
  
  <div className="dvd-credentials-subsection">
    <h4 className="dvd-credentials-subtitle">Document Verification</h4>
    <div className="dvd-documents-grid">
      <div className="dvd-document-item">
        {renderDocumentLink(doctor.licenseDocumentUrl, extractFilenameFromUrl(doctor.licenseDocumentUrl))}
      </div>
      <div className="dvd-document-item">
        {renderDocumentLink(doctor.educationCertificateUrl, extractFilenameFromUrl(doctor.educationCertificateUrl))}
      </div>
      <div className="dvd-document-item">
        {renderDocumentLink(doctor.diplomaUrl || doctor.educationCertificateUrl, extractFilenameFromUrl(doctor.diplomaUrl || doctor.educationCertificateUrl))}
      </div>
      <div className="dvd-document-item">
        {renderDocumentLink(doctor.resumeUrl || doctor.additionalDocumentUrls?.[0], extractFilenameFromUrl(doctor.resumeUrl || doctor.additionalDocumentUrls?.[0]))}
      </div>
    </div>
  </div>
</div>
          </div>

          {/* CONDITIONAL: Only show verification form for pending doctors */}
          {/* 🆕 FIXED: Show buttons for BOTH pending verification AND edit mode */}
{(isPendingVerification() || isEditMode) && (
  <div className="dvd-verification-form-section">
    <div className="dvd-form-card">
      <div className="dvd-verification-actions">
        {isEditMode ? (
          // Edit Mode - Show Save/Cancel buttons
          <>
            <div className="dvd-form-actions">
              <button 
                onClick={handleCancelEdit}
                className="dvd-btn-primary dvd-btn-cancel-edit"
                disabled={isSaving}
              >
                {isSaving ? 'Processing...' : 'Cancel'}
              </button>
              
              <button 
                onClick={handleSaveEdit}
                className="dvd-btn-primary dvd-btn-save-edit"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        ) : (
          // Pending Verification - Show Approve/Reject buttons
          <>
            
            <div className="dvd-form-actions">
              <button 
                onClick={() => handleVerification('approved')}
                className="dvd-btn-primary dvd-btn-approve"
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Approve Application'}
              </button>
              
              <button 
                onClick={() => handleRejection()}
                className="dvd-btn-primary dvd-btn-reject"
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Reject Application'}
              </button>
            </div>
          </>
        )}
        
        {error && (
          <div className="dvd-form-error">
            <span className="dvd-error-icon">⚠️</span>
            <span className="dvd-error-text">{error}</span>
          </div>
        )}
      </div>
    </div>
  </div>
)}
        </div>
      )}

      {/* Document Viewer Modal */}
      {isModalOpen && currentDocument && (
        <DocumentViewerModal
          isOpen={isModalOpen}
          onClose={closeDocumentModal}
          document={currentDocument}
        />
      )}

      <RejectionModal
          isOpen={isRejectionModalOpen}
          onClose={() => setIsRejectionModalOpen(false)}
          onSubmit={handleRejectionSubmit}
        />


    </div>
  );
};

const RejectionModal = ({ isOpen, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  
  if (!isOpen) return null;
  
  return (
    <div className="dvd-modal-backdrop">
      <div className="dvd-modal-container">
        <div className="dvd-modal-header">
          <h3>Please provide a reason for rejecting this application</h3>
        </div>
        <div className="dvd-modal-body">
          <textarea
            className="dvd-modal-textarea"
            placeholder="Enter rejection reason..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={5}
          />
        </div>
        <div className="dvd-modal-footer">
          <button 
            className="dvd-modal-button dvd-modal-cancel" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="dvd-modal-button dvd-modal-submit" 
            onClick={() => onSubmit(reason)}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorVerificationDetails;